/**
 * OperationsFilterBar — a SearchFilterBar pre-configured with the three
 * filters every operations list page shares (dateRange / subject / user).
 *
 * Page-specific filters are passed via `extraDefs` and inserted between
 * subject and email, mirroring the AnalysisList layout. Pages still own
 * the filter state and handle URL sync via `syncOperationsFiltersToUrl`
 * from `./operationsFilters`.
 */

import { useMemo } from 'react';
import { SearchFilterBar, type FilterDef } from '@shared-ui/components';
import { type UserSearchOption } from '@shared-ui/user-picker';
import { ScopedUserSearchFilter } from '../../components/ScopedUserSearchFilter';
import { COMMON_OPERATIONS_FILTER_DEFS } from './operationsFilters';

interface Props {
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  /** Page-specific filters inserted between subject and email. */
  extraDefs?: FilterDef[];
  /** Drop the user filter entirely — for lists with no user dimension. */
  hideEmail?: boolean;
  /** Pick/clear a user, by ID. Providing this swaps the plain email text box for
   *  the UserSearchFilter, so the admin can type part of a name OR an email and
   *  choose a real person instead of having to know the exact address.
   *  A pick applies immediately (like the question filter), which is why it
   *  does not go through `onChange` with the drafted select filters. */
  onUserChange?: (userId: string) => void;
  /** Users offered before anything is typed — normally the rows on the current
   *  page, so the common pick costs no request. */
  userSuggestions?: UserSearchOption[];
  loading?: boolean;
  trailingActions?: React.ReactNode;
  /** Extra controls rendered between the built-in filters and the Fetch button
   *  (e.g. the question search filter). Parent owns their state. */
  children?: React.ReactNode;
}

export function OperationsFilterBar({
  values, onChange, extraDefs, hideEmail, onUserChange, userSuggestions, loading, trailingActions, children,
}: Props) {
  // The lookup replaces the text box when the page wires it up; `hideEmail`
  // still drops the user dimension altogether.
  const useLookup = !hideEmail && !!onUserChange;
  const filterDefs = useMemo<FilterDef[]>(() => [
    COMMON_OPERATIONS_FILTER_DEFS.dateRange,
    COMMON_OPERATIONS_FILTER_DEFS.subject,
    ...(extraDefs ?? []),
    ...(hideEmail || useLookup ? [] : [COMMON_OPERATIONS_FILTER_DEFS.email]),
  ], [extraDefs, hideEmail, useLookup]);

  return (
    <SearchFilterBar
      filters={filterDefs}
      values={values}
      onChange={onChange}
      loading={loading}
      loadingLabel="Fetching..."
      disableFetchWhenClean
      hideSearch
      trailingActions={trailingActions}
    >
      {useLookup && (
        <ScopedUserSearchFilter
          /* Keyed on the ID, not the email. A school roster account has no
             email, so an email-keyed pick emitted '' — indistinguishable from
             clearing — and choosing a student appeared to do nothing. */
          valueKey="userId"
          value={values.userId ?? ''}
          onChange={onUserChange!}
          suggestions={userSuggestions}
          suggestionsLabel="Users on this page"
          valueText={userSuggestions?.find(o => o.userId === values.userId)?.name}
          placeholder="Search user…"
        />
      )}
      {children}
    </SearchFilterBar>
  );
}
